import { useAuth } from '../../auth/AuthProvider.jsx';
import { Permission } from '../../auth/roles.js';

// Visual style shared by both action sections. Light bordered card to match
// the rest of the app's "panel" aesthetic without pulling in the dashboard's
// scoped .db-card styles.
const CARD_STYLE = {
  border: '1px solid #dee2e6',
  borderRadius: 6,
  padding: 16,
  marginBottom: 16,
  background: '#fff',
};

const HEADER_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 4,
};

const TITLE_STYLE = { margin: 0, fontSize: 15, fontWeight: 600 };
const DESCRIPTION_STYLE = { margin: '0 0 12px', fontSize: 12, color: '#666' };
const HELPER_STYLE = { fontSize: 11, color: '#888' };

const BADGE_DATES = { background: '#cce5ff', color: '#004085' };
const BADGE_NO_DATES = { background: '#e2e3e5', color: '#6c757d' };

// Each non-date action gets its own column with the button + a short helper
// caption directly underneath, so the "no dates" assertion is impossible to miss.
const ACTION_COL_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  maxWidth: 280,
};

export function ExtractionForm({
  startDate, setStartDate, endDate, setEndDate,
  onExtract, onIncremental, onSyncNs, onReprocess, onQuickReprocess,
  loading, syncLoading, reprocessLoading,
  disabledByConflict = false,
}) {
  const { hasPermission } = useAuth();
  const canRun       = hasPermission(Permission.RUN_PIPELINE);
  const canReprocess = hasPermission(Permission.REPROCESS_BILLING);

  const busy             = loading || syncLoading || reprocessLoading || disabledByConflict;
  // Today (UTC) as YYYY-MM-DD — caps both pickers so a future date can't be
  // chosen, and the end picker can't precede the start. ISO date strings sort
  // lexicographically, so plain string comparison is correct here.
  const todayStr         = new Date().toISOString().slice(0, 10);
  const badRange         = Boolean(startDate && endDate && endDate < startDate);
  const futureDate       = Boolean(
    (startDate && startDate > todayStr) || (endDate && endDate > todayStr),
  );
  const disableExtract   = busy || !startDate || !endDate || badRange || futureDate;
  const disableNoOp      = busy;

  const showOtherOps = canRun || (canReprocess && onReprocess);

  return (
    <div>
      {/* ── Section 1: Date-Based Extraction ─────────────────────────────── */}
      <div style={CARD_STYLE}>
        <div style={HEADER_STYLE}>
          <h3 style={TITLE_STYLE}>Date-Based Extraction</h3>
          <span className="badge" style={BADGE_DATES}>Uses dates</span>
        </div>
        <p style={DESCRIPTION_STYLE}>
          Use this when you want to extract Dayforce raw data for a specific date range.
        </p>

        <div className="form-row" style={{ marginBottom: 6 }}>
          <div className="form-group">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              max={endDate || todayStr}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              min={startDate || undefined}
              max={todayStr}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          {canRun && (
            <button
              className="btn btn-primary"
              onClick={onExtract}
              disabled={disableExtract}
            >
              {loading ? 'Pipeline Running...' : 'Extract Raw Data by Date Range'}
            </button>
          )}
        </div>
        {canRun && badRange && (
          <div style={{ ...HELPER_STYLE, color: '#b00020' }}>
            End date cannot be earlier than start date.
          </div>
        )}
        {canRun && futureDate && (
          <div style={{ ...HELPER_STYLE, color: '#b00020' }}>
            Dates cannot be in the future.
          </div>
        )}
        {canRun && !badRange && !futureDate && (
          <div style={HELPER_STYLE}>Uses the selected start and end dates.</div>
        )}
      </div>

      {/* ── Section 2: Other Data Operations (no dates) ──────────────────── */}
      {showOtherOps && (
        <div style={CARD_STYLE}>
          <div style={HEADER_STYLE}>
            <h3 style={TITLE_STYLE}>Other Data Operations</h3>
            <span className="badge" style={BADGE_NO_DATES}>No dates</span>
          </div>
          <p style={DESCRIPTION_STYLE}>
            These actions do not use the selected date range.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {canRun && (
              <div style={ACTION_COL_STYLE}>
                <button
                  className="btn btn-success"
                  onClick={onIncremental}
                  disabled={disableNoOp}
                >
                  {loading ? 'Extracting...' : 'Extract New Data Since Last Run'}
                </button>
                <div style={HELPER_STYLE}>
                  Pulls only new or changed Dayforce data since the exact moment the
                  last successful run started (time-precise). Always runnable, even
                  seconds after the previous run.
                </div>
              </div>
            )}
            {canRun && (
              <div style={ACTION_COL_STYLE}>
                <button
                  className="btn btn-primary"
                  onClick={onSyncNs}
                  disabled={disableNoOp}
                >
                  {syncLoading ? 'Syncing...' : 'Sync NetSuite Reference Data'}
                </button>
                <div style={HELPER_STYLE}>
                  Refreshes NetSuite reference data. Does not use dates.
                </div>
              </div>
            )}
            {canReprocess && onQuickReprocess && (
              <div style={ACTION_COL_STYLE}>
                <button
                  className="btn btn-success"
                  onClick={onQuickReprocess}
                  disabled={disableNoOp}
                  title="Rebuilds only the employees/weeks whose Dayforce data changed since the last reprocess. Falls back with a message when rules or NetSuite data changed (those need a full reprocess)."
                >
                  {reprocessLoading ? 'Reprocessing...' : 'Quick Reprocess Billing'}
                </button>
                <div style={HELPER_STYLE}>
                  Rebuilds only what changed since the last reprocess (full or quick).
                  Seconds, not minutes. Asks for a full reprocess when rules,
                  include/exclude toggles, or NetSuite data changed. Rare edge cases
                  (e.g. a day&apos;s data deleted entirely upstream) are reconciled by
                  the nightly full reprocess.
                </div>
              </div>
            )}
            {canReprocess && onReprocess && (
              <div style={ACTION_COL_STYLE}>
                <button
                  className="btn btn-primary"
                  onClick={onReprocess}
                  disabled={disableNoOp}
                >
                  {reprocessLoading ? 'Reprocessing...' : 'Reprocess Billing Records'}
                </button>
                <div style={HELPER_STYLE}>
                  Rebuilds billing records from already extracted data. Does not use dates.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
